import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { ToastrService } from 'ngx-toastr'
import { IAuthData } from '../interfaces/IAuthData'
import { IAuthResponse } from '../interfaces/IAuthResponse'
import { EnvironmentsHelperService } from '../../../../../environments/core/services/environments-helper.service'
import { IAuthResponseData } from '../interfaces/IAuthResponseData'
import { IAuthRefreshResponse } from '../interfaces/IAuthRefreshResponse'
import { ITwoFactorSetupResponse } from '../interfaces/ITwoFactorSetupResponse'
import { ITwoFactorEnableResponse } from '../interfaces/ITwoFactorEnableResponse'
import { ITwoFactorStatusResponse } from '../interfaces/ITwoFactorStatusResponse'
import { CURRENT_USER_STORAGE_KEY } from '../consts/storage-keys'
import { UserService } from '../../../general/users/core/services/user.service'

@Injectable({
	providedIn: 'root',
})
export class AuthenticationService {
	protected readonly json: JSON = JSON

	private _currentUser: BehaviorSubject<IAuthResponseData | null> = new BehaviorSubject<IAuthResponseData | null>(
		null,
	)

	public currentUser$: Observable<IAuthResponseData | null>

	constructor(
		private _http: HttpClient,
		private _environmentsHelperService: EnvironmentsHelperService,
		private _userService: UserService,
		private _toastrService: ToastrService,
	) {
		this._initUser()
	}

	get currentUserValue(): IAuthResponseData | null {
		return this._currentUser.value
	}

	private _initUser(): void {
		let user: IAuthResponseData | null = null
		const stored: string | null = localStorage.getItem(CURRENT_USER_STORAGE_KEY)
		if (stored) {
			try {
				const parsed: IAuthResponseData = this.json.parse(stored)
				if (parsed?.tokens?.access_token && parsed?.tokens?.refresh_token) {
					user = parsed
				} else {
					localStorage.removeItem(CURRENT_USER_STORAGE_KEY)
				}
			} catch {
				localStorage.removeItem(CURRENT_USER_STORAGE_KEY)
			}
		}
		this._currentUser = new BehaviorSubject<IAuthResponseData | null>(user)
		this.currentUser$ = this._currentUser.asObservable()
	}

	private _storeUser(user: IAuthResponseData): void {
		localStorage.setItem(CURRENT_USER_STORAGE_KEY, this.json.stringify(user))
		this._currentUser.next(user)
	}

	// Шаг 1 логина. Возвращает полный ответ: при 2FA поля data=null, pre_auth_token заполнен.
	public login(data: IAuthData): Observable<IAuthResponse> {
		return this._http
			.post<IAuthResponse>(`${this._environmentsHelperService.authUrl}/api/v1/auth/login`, { ...data, client: 'admin' })
			.pipe(
				tap((response: IAuthResponse): void => {
					if (response.data) {
						this._storeUser(response.data)
					}
				}),
			)
	}

	// Шаг 2 логина — подтверждение TOTP/email кода. pre_auth_token передаётся в body.
	public login2fa(preAuthToken: string, code: string): Observable<IAuthResponseData> {
		return this._http
			.post<IAuthResponseData>(`${this._environmentsHelperService.authUrl}/api/v1/auth/login/2fa`, {
				pre_auth_token: preAuthToken,
				code,
			})
			.pipe(
				tap((user: IAuthResponseData): void => {
					this._storeUser(user)
				}),
			)
	}

	// Повторная отправка email-кода для шага 2 логина (только method=email)
	public resendLoginCode(preAuthToken: string): Observable<{ sent: boolean; masked_email: string }> {
		return this._http.post<{ sent: boolean; masked_email: string }>(
			`${this._environmentsHelperService.authUrl}/api/v1/auth/login/2fa/resend`,
			{ pre_auth_token: preAuthToken },
		)
	}

	// Инициализация TOTP-настройки. preAuthToken передаётся когда пользователь не залогинен.
	// Когда залогинен — интерцептор автоматически добавляет access_token.
	public setup2fa(method: 'totp', password: string, preAuthToken?: string): Observable<ITwoFactorSetupResponse> {
		const options = preAuthToken
			? { headers: new HttpHeaders({ Authorization: `Bearer ${preAuthToken}` }) }
			: {}
		return this._http.post<ITwoFactorSetupResponse>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/setup`,
			{ method, password },
			options,
		)
	}

	// Активация 2FA после подтверждения кода из приложения. Возвращает backup-коды.
	public enable2fa(code: string, password: string, preAuthToken?: string): Observable<ITwoFactorEnableResponse> {
		const options = preAuthToken
			? { headers: new HttpHeaders({ Authorization: `Bearer ${preAuthToken}` }) }
			: {}
		return this._http.post<ITwoFactorEnableResponse>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/enable`,
			{ code, password, client: 'admin' },
			options,
		)
	}

	// Запрос email-кода для управления 2FA (disable, regenerate_backup_codes, manage_clients)
	public challenge2fa(action: 'disable' | 'regenerate_backup_codes' | 'manage_clients'): Observable<{ sent: boolean; masked_email: string }> {
		return this._http.post<{ sent: boolean; masked_email: string }>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/challenge`,
			{ action },
		)
	}

	// Отключение 2FA (для обычных пользователей и миграции staff)
	public disable2fa(code: string): Observable<{ success: boolean; message: string }> {
		return this._http.post<{ success: boolean; message: string }>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/disable`,
			{ code },
		)
	}

	// Перевыпуск backup-кодов. Требует TOTP-код. Текущая сессия остаётся активной.
	public regenerateBackupCodes(code: string): Observable<{ backup_codes: string[] }> {
		return this._http.post<{ backup_codes: string[] }>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/backup-codes/regenerate`,
			{ code },
		)
	}

	// Получить статус 2FA для текущего пользователя
	public get2faStatus(): Observable<ITwoFactorStatusResponse> {
		return this._http.get<ITwoFactorStatusResponse>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/status`,
		)
	}

	// Обновить список клиентов 2FA
	public updateClients(clients: string[], code: string): Observable<ITwoFactorStatusResponse> {
		return this._http.put<ITwoFactorStatusResponse>(
			`${this._environmentsHelperService.authUrl}/api/v1/2fa/clients`,
			{ clients, code },
		)
	}

	public refreshToken(): Observable<IAuthRefreshResponse> {
		return this._http
			.post<IAuthRefreshResponse>(`${this._environmentsHelperService.authUrl}/api/v1/auth/refresh`, {})
			.pipe(
				tap((tokens: IAuthRefreshResponse): void => {
					const currentUser: IAuthResponseData | null = this._currentUser.value
					if (!currentUser) {
						return
					}
					const updatedUser: IAuthResponseData = {
						...currentUser,
						tokens: {
							access_token: tokens.access_token,
							refresh_token: tokens.refresh_token,
						},
					}
					localStorage.setItem(CURRENT_USER_STORAGE_KEY, this.json.stringify(updatedUser))
					this._currentUser.next(updatedUser)
				}),
			)
	}

	public logout(): void {
		this._http.post<void>(`${this._environmentsHelperService.authUrl}/api/v1/auth/logout`, {}).subscribe(
			null,
			(error: HttpErrorResponse): void => {
				this._toastrService.error('Не удалось завершить сессию на сервере')
				console.error('Logout error', error)
			},
		)
		localStorage.removeItem(CURRENT_USER_STORAGE_KEY)
		localStorage.removeItem('pageSizes')
		this._currentUser.next(null)

		// userDetail$ — root-синглтон и переживает выход внутри SPA. Без сброса CurrentUserResolver
		// отдаёт закэшированного прошлого пользователя, и следующий вход получает его роль:
		// чужой ярлык в навбаре, чужое меню и чужие права в RoleGuard до перезагрузки страницы.
		this._userService.userDetail$.next(null)
	}
}
